import {useCallback, useEffect, useRef, useState, type MouseEvent as ReactMouseEvent, type ReactNode} from 'react'
import {createPortal} from 'react-dom'
import {Link as RouterLink} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {generateIconZip, type IconComposeState} from '../../../common/utils/iconRenderer'
import type {AppIconPlatformId} from '../../../common/constants/appIconSizes'
import {reportError} from '../../../common/errors/reportError'
import {addBreadcrumb} from '../../../common/errors/breadcrumb'
import {trackEvent} from '../../../common/analytics/trackEvent'
import {downloadFile} from '../../../common/utils/downloadFile'

interface IconExportDialogProps {
  state: IconComposeState
  selectedPlatforms: ReadonlySet<AppIconPlatformId>
  onClose: () => void
}

const FREE_MIN_DURATION_MS = 7000
const FREE_MAX_DURATION_MS = 10000
const FREE_PROGRESS_CEILING = 95
const pickDuration = (): number => FREE_MIN_DURATION_MS + Math.random() * (FREE_MAX_DURATION_MS - FREE_MIN_DURATION_MS)

const iconStrokeProps = {
  width: 18,
  height: 18,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
}

interface AdFeat {
  key: string
  icon: ReactNode
}

// Icon-generator visitors arrived for one thing — icons. They may not know we
// also make App Store / Play Store screenshots, which is what 'the main app'
// does. The ad pitches that adjacent need instead of the generic editor sell.
const AD_FEATS: AdFeat[] = [
  {
    key: 'mockup',
    icon: (
      <svg {...iconStrokeProps}>
        <rect x="6" y="2" width="12" height="20" rx="2.5" />
        <path d="M11 18h2" />
      </svg>
    ),
  },
  {
    key: 'carousel',
    icon: (
      <svg {...iconStrokeProps}>
        <rect x="2" y="5" width="6" height="14" rx="1.2" />
        <rect x="9" y="5" width="6" height="14" rx="1.2" />
        <rect x="16" y="5" width="6" height="14" rx="1.2" />
      </svg>
    ),
  },
  {
    key: 'free',
    icon: (
      <svg {...iconStrokeProps}>
        <path d="M20.6 13.4l-7.2 7.2a2 2 0 01-2.8 0L3 12.8V4h8.8l8.8 8.8a2 2 0 010 2.6z" />
        <circle cx="8.5" cy="8.5" r="1.3" />
      </svg>
    ),
  },
]

const arrowIcon = (
  <svg
    width={13}
    height={13}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M5 12h14" />
    <path d="M13 5l7 7-7 7" />
  </svg>
)

const IconExportDialog = ({state, selectedPlatforms, onClose}: IconExportDialogProps) => {
  const {t} = useTranslation()
  const [progress, setProgress] = useState(0)
  const [ready, setReady] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const zipBlobRef = useRef<Blob | null>(null)
  const startRef = useRef<number | null>(null)
  const durationRef = useRef<number | null>(null)

  // Kick off ZIP generation in parallel with the synthetic timer.
  // Parent mounts/unmounts this component per open, so no reset effect is needed.
  useEffect(() => {
    let cancelled = false
    addBreadcrumb('icon-gen', 'zip-start', 'info', {platforms: selectedPlatforms.size})
    trackEvent('icon_zip_started', {platform_count: selectedPlatforms.size, gated: true})
    trackEvent('upsell_shown', {surface: 'icon_gen'})
    generateIconZip(state, selectedPlatforms)
      .then((blob) => {
        if (!cancelled) {
          zipBlobRef.current = blob
          addBreadcrumb('icon-gen', 'zip-ready', 'info', {size: blob.size})
          trackEvent('icon_zip_completed', {size_bytes: blob.size, platform_count: selectedPlatforms.size, gated: true})
        }
      })
      .catch((err) => {
        if (!cancelled) {
          reportError(err, 'icon-gen-zip', {platforms: [...selectedPlatforms]})
          setError(t('iconGenerator.download.failed'))
        }
      })
    return () => {
      cancelled = true
    }
  }, [state, selectedPlatforms, t])

  // Drive synthetic progress (ease-out cubic)
  useEffect(() => {
    if (ready) return
    if (startRef.current === null) startRef.current = performance.now()
    if (durationRef.current === null) durationRef.current = pickDuration()

    let rafId = 0
    const tick = () => {
      const start = startRef.current ?? performance.now()
      const duration = durationRef.current ?? FREE_MIN_DURATION_MS
      const elapsed = performance.now() - start
      const ratio = Math.min(1, elapsed / duration)
      const eased = 1 - Math.pow(1 - ratio, 3)
      const zipDone = zipBlobRef.current !== null
      const ceiling = zipDone ? 100 : FREE_PROGRESS_CEILING
      setProgress(Math.min(eased * 100, ceiling))

      if (elapsed >= duration && zipDone) {
        setProgress(100)
        setReady(true)
        return
      }
      rafId = requestAnimationFrame(tick)
    }
    rafId = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(rafId)
  }, [ready])

  // Esc to close
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [onClose])

  const handleDownload = useCallback(async () => {
    const blob = zipBlobRef.current
    if (!blob) return
    await downloadFile(blob, 'app-icons.zip')
    onClose()
  }, [onClose])

  const handleOverlayClick = useCallback(
    (e: ReactMouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) onClose()
    },
    [onClose],
  )

  return createPortal(
    <div className="icg-modal-overlay" role="dialog" aria-modal="true" onClick={handleOverlayClick}>
      <div className="icg-modal">
        <header className="icg-modal-header">
          <h3 className="icg-modal-title">
            {ready ? t('iconGenerator.dialog.readyTitle') : t('iconGenerator.dialog.title')}
          </h3>
          <button
            type="button"
            className="icg-modal-close"
            onClick={onClose}
            aria-label={t('iconGenerator.dialog.close')}
          >
            ×
          </button>
        </header>

        <div className="icg-modal-body">
          <div className="icg-modal-progress-side">
            <p className="icg-modal-sub">
              {ready ? t('iconGenerator.dialog.ready') : t('iconGenerator.dialog.preparing')}
            </p>

            {error && <p className="icg-error">{error}</p>}

            {!ready && !error && (
              <div className="icg-modal-progress-row">
                <div
                  className="icg-modal-progress"
                  role="progressbar"
                  aria-label={t('iconGenerator.dialog.progressLabel')}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={Math.round(progress)}
                >
                  <div className="icg-modal-progress-bar" style={{transform: `translateX(${-(100 - progress)}%)`}} />
                </div>
                <span className="icg-modal-progress-value">{Math.round(progress)}%</span>
              </div>
            )}

            {ready && !error && (
              <button type="button" className="icg-btn icg-btn-primary" onClick={handleDownload}>
                {t('iconGenerator.dialog.download')}
              </button>
            )}
          </div>

          <aside className="icg-modal-ad" aria-label={t('iconGenerator.dialog.ad.ariaLabel')}>
            <div className="icg-ad-header">
              <div className="icg-ad-eyebrow">{t('iconGenerator.dialog.ad.eyebrow')}</div>
              <div className="icg-ad-heading">{t('iconGenerator.dialog.ad.heading')}</div>
              <p className="icg-ad-sub">{t('iconGenerator.dialog.ad.sub')}</p>
            </div>
            <ul className="icg-ad-feats">
              {AD_FEATS.map(({key, icon}) => (
                <li key={key} className="icg-ad-feat">
                  <span className="icg-ad-feat-icon" aria-hidden>
                    {icon}
                  </span>
                  <div className="icg-ad-feat-body">
                    <div className="icg-ad-feat-label">{t(`iconGenerator.dialog.ad.feats.${key}.label`)}</div>
                    <div className="icg-ad-feat-desc">{t(`iconGenerator.dialog.ad.feats.${key}.desc`)}</div>
                  </div>
                </li>
              ))}
            </ul>
            {/* Opens in a new tab: clicking this while the ZIP is still generating
                must not navigate the current tab away and interrupt it. */}
            <RouterLink to="/free" className="icg-ad-cta" target="_blank" rel="noopener noreferrer">
              <span className="icg-ad-cta-main">
                {t('iconGenerator.dialog.ad.cta')}
                {arrowIcon}
              </span>
              <span className="icg-ad-cta-hint">{t('iconGenerator.dialog.ad.ctaHint')}</span>
            </RouterLink>
          </aside>
        </div>
      </div>
    </div>,
    document.body,
  )
}

export default IconExportDialog
